import moment from 'moment';
import { firestore } from 'firebaseConfig';
import { DateTime } from 'luxon';
import TimeUtils from 'utils/TimeUtils';
import SettingsUtils from 'utils/SettingsUtils';

function randomBytes(length) {
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  return bytes;
}

class EntityUtils {
  isAddressValid(countryCode, address) {
    if (countryCode === 'US') {
      try {
        let valid = true;
        if (address !== undefined && address !== null) {
          if (
            address.addressLine1 === undefined ||
            address.addressLine1 === '' ||
            address.addressLine1 === 'undefined'
          ) {
            valid = false;
          }
          if (
            address.city === undefined ||
            address.city === '' ||
            address.city === 'undefined'
          ) {
            valid = false;
          }
          if (
            address.state === undefined ||
            address.state === '' ||
            address.state === 'undefined'
          ) {
            valid = false;
          }
          if (
            address.zip === undefined ||
            address.zip === '' ||
            address.zip === 'undefined' ||
            address.zip.length !== 5
          ) {
            valid = false;
          }
        } else {
          valid = false;
        }
        return valid;
      } catch (e) {
        console.error(e);
        return false;
      }
    } else {
      try {
        let valid = true;
        if (address !== undefined && address !== null) {
          if (
            address.addressLine1 === undefined ||
            address.addressLine1 === '' ||
            address.addressLine1 === 'undefined'
          ) {
            valid = false;
          }
          if (
            address.city === undefined ||
            address.city === '' ||
            address.city === 'undefined'
          ) {
            valid = false;
          }
          if (
            address.state === undefined ||
            address.state === '' ||
            address.state === 'undefined'
          ) {
            valid = false;
          }
          if (
            address.zip === undefined ||
            address.zip === '' ||
            address.zip === 'undefined' ||
            address.zip.length !== 7
          ) {
            valid = false;
          }
        } else {
          valid = false;
        }
        return valid;
      } catch (e) {
        console.error(e);
        return false;
      }
    }
  }

  isAddressZipValid(countryCode, zip) {
    if (countryCode === 'US') {
      try {
        let valid = true;
        if (
          zip === undefined ||
          zip === '' ||
          zip === 'undefined' ||
          zip.length !== 5
        ) {
          valid = false;
        }
        return valid;
      } catch (e) {
        console.error(e);
        return false;
      }
    } else {
      try {
        let valid = true;
        if (
          zip === undefined ||
          zip === '' ||
          zip === 'undefined' ||
          zip.length !== 7
        ) {
          valid = false;
        }
        return valid;
      } catch (e) {
        console.error(e);
        return false;
      }
    }
  }

  getBusinessName(entity) {
    try {
      let name = undefined;
      if (entity !== undefined && entity !== null) {
        if (entity.isBusiness !== undefined && entity.isBusiness) {
          if (
            entity.businessName !== undefined &&
            entity.businessName !== '' &&
            (typeof entity.businessName === 'string' ||
              entity.businessName instanceof String)
          ) {
            name = entity.businessName.trim();
          }
        }
      }

      if (name === '') {
        name = undefined;
      }

      return name;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  getName(entity) {
    try {
      let name = '';
      if (entity !== undefined && entity !== null) {
        if (entity.name !== undefined) {
          if (
            entity.name.first !== undefined &&
            entity.name.last !== undefined
          ) {
            name = `${entity.name.first.trim()} ${entity.name.last.trim()}`;
          } else if (
            entity.name.first === undefined &&
            entity.name.last !== undefined
          ) {
            name = entity.name.last.trim();
          } else if (
            entity.name.first !== undefined &&
            entity.name.last === undefined
          ) {
            name = entity.name.first.trim();
          } else {
            name = '';
          }
        }
      }

      return name;
    } catch (e) {
      console.error(e);
      return '';
    }
  }

  getCustomerName(customer) {
    try {
      let name = this.getBusinessName(customer);
      if (name === undefined) {
        name = this.getName(customer);
      }
      return name;
    } catch (e) {
      console.error(e);
      return '';
    }
  }

  generateWebhookEntry() {
    return {
      id: this.generateID(),
      event: 'None',
      eventName: 'My Webhook',
      url: '',
    };
  }

  generateStorefrontConfiguration(company) {
    return {
      companyName: company.businessName,
      appearance: {},
      id: this.generateID(),
      staged: true,
    };
  }

  generateEmbedConfiguration(company) {
    return {
      companyId: company.id,
      embedName: 'Untitled Embed Configuration',
      id: this.generateID(),
      selectors: [],
      staged: true,
    };
  }

  generateEmbedConfigurationSelector(company) {
    return {
      classes: '',
      configId: '',
      workflowId: '',
      prefills: {},
    };
  }

  generateID() {
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let autoId = '';
    while (autoId.length < 20) {
      const bytes = randomBytes(40);
      bytes.forEach(b => {
        // Length of `chars` is 62. We only take bytes between 0 and 62*4-1
        // (both inclusive). The value is then evenly mapped to indices of `char`
        // via a modulo operation.
        const maxValue = 62 * 4 - 1;
        if (autoId.length < 20 && b <= maxValue) {
          autoId += chars.charAt(b % 62);
        }
      });
    }

    return autoId;
  }

  generateShortID() {
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let autoId = '';
    while (autoId.length < 5) {
      const bytes = randomBytes(40);
      bytes.forEach(b => {
        // Length of `chars` is 62. We only take bytes between 0 and 62*4-1
        // (both inclusive). The value is then evenly mapped to indices of `char`
        // via a modulo operation.
        const maxValue = 62 * 4 - 1;
        if (autoId.length < 5 && b <= maxValue) {
          autoId += chars.charAt(b % 62);
        }
      });
    }

    return autoId;
  }

  generateWorkflowPrepPage() {
    let prepPage = {
      type: 'prep_page',
      id: this.generateID(),
      attributes: {
        prepFields: ['zip'],
        title: "Let's get started.",
        introduction:
          "Please enter an address below and we will determine if you're in our service area",
        onInsufficientPrequisites: {
          redirectTo: null,
        },
      },
    };

    return prepPage;
  }

  generateWorkflowPrepPageForm() {
    let form = {
      title: 'Service Address Lookup',
      progressionButtonProperties: {
        label: 'Continue',
      },
      attributes: {
        contentType: 'template',
        showFooter: false,
        addressConfirmationDisplay: 'show_map',
      },
      content: [this.generateWorkflowPrepPage()],
      onSubmitSuccessful: {
        proceedTo: null,
      },
    };

    form.id = this.generateID();
    return form;
  }

  generateWorkflowProductList() {
    let productList = {
      type: 'product_list',
      attributes: {
        itemDisplay: 'list',
        maxQuantityPerItem: 1,
        multiselect: false,
        selectionIsRequired: true,
        productIds: [],
      },
      header: {},
    };

    productList.id = this.generateID();
    return productList;
  }

  generateWorkflowQuestionnaire() {
    let questionnaire = {
      type: 'question_template',
      attributes: {
        question: 'This is the question prompt, the following are the answers:',
        questionId: 'question_' + this.generateID(),
        answers: [
          {
            uuid: this.generateID(),
            id: 'answer_1',
            text: 'This is answer 1',
          },
          {
            uuid: this.generateID(),
            id: 'answer_2',
            text: 'This is answer 2',
          },
          {
            uuid: this.generateID(),
            id: 'answer_3',
            text: 'This is answer 3',
          },
        ],
      },
      header: {},
    };

    questionnaire.id = this.generateID();
    return questionnaire;
  }

  generateWorkflowProductListForm() {
    let form = {
      title: 'Product List',
      progressionButtonProperties: {
        label: 'Continue',
      },
      attributes: {
        contentType: 'template',
      },
      content: [this.generateWorkflowProductList()],
      onSubmitSuccessful: {
        proceedTo: null,
      },
    };

    form.id = this.generateID();
    return form;
  }

  generateWorkflowQuestionnaireForm() {
    let form = {
      title: 'Question',
      progressionButtonProperties: {
        label: 'Continue',
      },
      attributes: {
        contentType: 'template',
      },
      content: [this.generateWorkflowQuestionnaire()],
      onSubmitSuccessful: {
        proceedTo: null,
      },
    };

    form.id = this.generateID();
    return form;
  }

  generateCustomFormContent() {
    let customForm = {
      type: 'custom_form',
      attributes: {
        inputs: [
          {
            key: 'heading',
            type: 'heading',
            text: 'Tell us about yourself',
          },
          {
            key: 'first_name',
            type: 'text_input',
            placeholder: 'First Name',
            identifier: 'contact_first_name',
            title: 'First Name',
          },
          {
            key: 'last_name',
            type: 'text_input',
            placeholder: 'Last Name',
            identifier: 'contact_last_name',
            title: 'Last Name',
          },
          {
            key: 'email',
            type: 'text_input',
            placeholder: 'you@email.com',
            identifier: 'contact_email',
            title: 'Email',
            validation: 'email',
          },
          {
            key: 'phone',
            type: 'text_input',
            placeholder: '(555) 555-5555',
            identifier: 'contact_phone_number',
            title: 'Phone Number',
            validation: 'phone',
          },
        ],
      },
      header: {},
    };

    customForm.id = this.generateID();
    return customForm;
  }

  generateCustomFormTemplate() {
    let form = {
      title: 'Contact Sheet',
      progressionButtonProperties: {
        label: 'Continue',
      },
      attributes: {
        contentType: 'template',
      },
      content: [this.generateCustomFormContent()],
      onSubmitSuccessful: {
        proceedTo: null,
      },
    };

    form.id = this.generateID();
    return form;
  }

  generateWorkflowLandingPageFormNoService(company) {
    let form = {
      attributes: {
        contentType: 'template',
        showInvoice: false,
      },
      content: [this.generateWorkflowLandingPageNoService(company)],
      id: this.generateID(),
      onSubmitSuccessful: {
        returnTo: null,
      },
      progressionButtonProperties: {
        icon: 'arrow-circle-left',
        label: 'Try a Different Address',
      },
      title: 'No Service Available',
    };

    return form;
  }

  generateWorkflowLandingPageForm(company) {
    let form = {
      attributes: {
        contentType: 'template',
        showInvoice: false,
      },
      content: [this.generateWorkflowLandingPage(company)],
      id: this.generateID(),
      onSubmitSuccessful: {
        returnTo: null,
      },
      title: 'Landing Page',
    };

    return form;
  }

  generateWorkflowLandingPageNoService(company) {
    let phoneNumber = '[Phone Number]';
    if (company && company.properties && company.properties.phone) {
      phoneNumber = `(${company.properties.phone.areaCode}) ${
        company.properties.phone.number
      }`;
    }

    let landingPage = {
      id: this.generateID(),
      text: `Sorry, we can't offer products for your area through this checkout process. Please try calling us at ${phoneNumber} and we'll do our best to help you!`,
      title: 'Service Not Available',
      type: 'landing',
    };

    return landingPage;
  }

  generateWorkflowLandingPage(company) {
    let landingPage = {
      id: this.generateID(),
      text: `Landing Page Text`,
      title: 'Landing Page Title',
      subtitle: 'Landing Page Subtitle',
      buttonText: 'Landing Page Button',
      type: 'landing',
    };

    return landingPage;
  }

  generateWorkflowContactDeliveryForm() {
    let form = {
      attributes: {
        contentType: 'template',
      },
      title: 'Contact & Delivery',
      id: this.generateID(),
      content: [this.generateWorkflowContactDelivery()],
      onSubmitSuccessful: {
        proceedTo: null,
      },
      progressionButtonProperties: {
        label: 'Continue',
      },
    };

    return form;
  }

  generateWorkflowContactDelivery() {
    let contactDelivery = {
      attributes: {
        maxServiceDayRange: 7,
        timeframes: {
          enabled: true,
        },
      },
      id: this.generateID(),
      type: 'contact_delivery_template',
    };

    return contactDelivery;
  }

  generateWorkflowTerms() {
    let terms = {
      attributes: {
        externalLinkButtonText: 'View Agreement',
        notes: 'I have read and agree to the rental agreement',
        showExternalLink: true,
        termsText:
          '<strong>Please read the following terms and conditions carefully:</strong> <br><br>Place your terms and conditions here.',
        id: this.generateID(),
      },
      type: 'terms_block',
    };

    return terms;
  }

  generateWorkflowTermsForm() {
    let form = {
      attributes: {
        contentType: 'template',
      },
      content: [this.generateWorkflowTerms()],
      id: this.generateID(),
      onSubmitSuccessful: {
        proceedTo: null,
      },
      title: 'Terms and Agreement',
    };

    return form;
  }

  generateWorkflowPaymentContent() {
    let paymentContent = {
      id: this.generateID(),
      type: 'payment_template',
      attributes: {
        disclosureType: 'no_disclosure',
      },
    };

    return paymentContent;
  }

  generateWorkflowPaymentForm() {
    let form = {
      id: this.generateID(),
      onSubmitSuccessful: {
        action: 'submit_order',
      },
      progressionButtonProperties: {
        icon: 'lock',
        label: 'Place Order',
      },
      content: [this.generateWorkflowPaymentContent()],
      title: 'Payment',
      attributes: {
        contentType: 'template',
      },
    };

    return form;
  }

  getWorkflowPaths(workflow) {
    const rootID = workflow.attributes.initialForm;

    const form = workflow.forms[rootID];
    let workflowPaths = {
      root: {
        type: 'root',
        id: rootID,
        paths: [],
        title: form.title,
        parentID: null,
      },
    };
    workflowPaths.root.paths = this.getFormPaths(workflow, form);

    return workflowPaths;
  }

  getFormPaths(workflow, form) {
    let paths = [];
    if (form) {
      if (form.onSubmitSuccessful && form.onSubmitSuccessful.proceedTo) {
        const childFormID = form.onSubmitSuccessful.proceedTo;
        const childForm = workflow.forms[childFormID];
        const childPaths = this.getFormPaths(workflow, childForm);
        paths.push({
          type: 'onSubmitSuccessful',
          id: childFormID,
          paths: childPaths,
          title: childForm.title,
          parentID: form.id,
        });
      }
    }

    return paths;
  }

  generateWorkflow(company) {
    let workflow = {
      forms: {},
      active: true,
    };

    let prepPage = this.generateWorkflowPrepPageForm();
    let productList = this.generateWorkflowProductListForm();
    let noServiceLanding = this.generateWorkflowLandingPageFormNoService(
      company,
    );
    let contactDeliveryForm = this.generateWorkflowContactDeliveryForm();
    let termsAgreementForm = this.generateWorkflowTermsForm();
    let paymentForm = this.generateWorkflowPaymentForm();

    // Successful prep sends to product list
    prepPage.onSubmitSuccessful.proceedTo = productList.id;

    // Failed prep sends to no service landing
    prepPage.content[0].attributes.onInsufficientPrequisites.redirectTo =
      noServiceLanding.id;

    // Return from landing back to prep page
    noServiceLanding.onSubmitSuccessful.returnTo = prepPage.id;

    // Product List -> Contact
    productList.onSubmitSuccessful.proceedTo = contactDeliveryForm.id;

    // Contact -> Terms
    contactDeliveryForm.onSubmitSuccessful.proceedTo = termsAgreementForm.id;

    // Terms -> Payment
    termsAgreementForm.onSubmitSuccessful.proceedTo = paymentForm.id;

    workflow.forms[prepPage.id] = prepPage;
    workflow.forms[productList.id] = productList;
    workflow.forms[noServiceLanding.id] = noServiceLanding;
    workflow.forms[contactDeliveryForm.id] = contactDeliveryForm;
    workflow.forms[termsAgreementForm.id] = termsAgreementForm;
    workflow.forms[paymentForm.id] = paymentForm;
    workflow.attributes = {
      serviceAreaParameters: {
        type: 'ZIP_CODE_WHITELIST',
        serviceAreaValue: {
          zipCodeWhitelist: [],
          countyWhitelist: [],
          radiusMiles: 10,
          radiusCenter: {
            lat: 34,
            lng: -87,
          },
          zoom: 12,
        },
      },
      initialForm: prepPage.id,
      version: '1',
      buildStrategy: 'streamlined',
    };
    workflow.id = this.generateID();
    workflow.hash = this.generateID();
    workflow.staged = true;
    workflow.staging = true;
    workflow.stagingProperties = {
      title: 'Our storefront is under construction!',
      description: 'Check back again soon',
    };

    return workflow;
  }

  generateAdditionalNotesSection() {
    return {
      id: this.generateID(),
      placeholder: 'Enter placeholder text here',
      metadata: {},
      required: false,
      text: '',
      label: 'note_' + this.generateShortID(),
      validationErrorMessage: 'Input in notes section is required',
    };
  }

  generateDiscount(companyId) {
    return {
      id: this.generateID(),
      active: true,
      company_id: companyId,
      allocation_limit: null,
      creation_timestamp: new Date().getTime(),
      creation_daystamp: Number(
        moment(new Date().getTime()).format('YYYYMMDD'),
      ),
      ends_on_date: null,
      starts_on_date: null,
      ends_on_time: null,
      starts_on_time: null,
      version: 1,
      title: null,
      codes: [],
      times_used: 0,
      description: null,
      prerequisites: [],
      applies_to_ids: [],
      discount_schemas: [],
    };
  }

  generateCodeData() {
    return {
      id: this.generateID(),
      code_text: null,
    };
  }

  generateDiscountApplicableProduct() {
    return {
      id: this.generateID(),
      product_id: null,
      variant_id: null,
    };
  }

  generateDiscountPrequisite() {
    return {
      id: this.generateID(),
      type: 'customer_met_total',
      properties: this.generateDiscountPrequisiteProperties(
        'customer_met_total',
      ),
    };
  }

  generateDiscountPrequisiteProperties(type) {
    if (type === 'customer_met_total') {
      return {
        total_value_to_meet: null,
        pretax: true,
        prefees: true,
      };
    }
  }

  generateDiscountSchema() {
    return {
      id: this.generateID(),
      type: 'flat_amount_discount',
      properties: this.generateDiscountSchemaProperties('flat_amount_discount'),
    };
  }

  generateDiscountSchemaProperties(type) {
    if (type === 'flat_amount_discount') {
      return {
        discount_amount: null,
      };
    }
  }

  syncVariantEffectConditionItemIds(variant, previousVariantId) {
    if (!variant?.effects || !variant?.id) {
      return variant;
    }

    Object.keys(variant.effects).forEach(key => {
      const effectType = variant.effects[key];
      if (!Array.isArray(effectType)) {
        return;
      }

      effectType.forEach(effect => {
        effect?.conditions?.forEach(condition => {
          if (condition?.type !== 'ITEM_IN_CART') {
            return;
          }

          if (
            !condition.itemId ||
            condition.itemId === variant.id ||
            (previousVariantId && condition.itemId === previousVariantId)
          ) {
            condition.itemId = variant.id;
          }
        });
      });
    });

    return variant;
  }

  generateProductSet(companyId) {
    return {
      company_id: companyId,
      active: true,
      id: this.generateID(),
      creation_daystamp: Number(
        moment(new Date().getTime()).format('YYYYMMDD'),
      ),
      creation_timestamp: new Date().getTime(),
      metadata: {},
      options_params: {},
      price_params: {
        params: {},
      },
      fee_definitions: {},
      subtitle: '',
      title: '',
      tags: [],
      type: '',
      staged: true,
    };
  }

  generateProductAttribute() {
    return {
      id: 'untitled_attribute',
      label: '',
      values: {},
      staged: true,
    };
  }

  generateProductPriceFactor() {
    return {
      staged: true,
      id: 'untitled_price_factor',
      skipMatrix: false,
      sendAs: 'untitled_price_factor',
    };
  }

  generateProductFee() {
    return {
      staged: true,
      id: this.generateID(),
      label: '',
      description: '',
      condition_type: 'flat_fee',
      price: 0,
      active: true,
      ranges: [],
      taxable: true,
      rangeValueKey: 'service_distance',
      price_params: {},
    };
  }

  generateProductVariant() {
    return {
      currency: 'USD',
      desc: null,
      active: true,
      featured_image: null,
      images: [],
      id: this.generateID(),
      metadata: {},
      options_values: {},
      price: null,
      price_type: 'FIXED',
      requires_shipping: false,
      sku: null,
      taxable: true,
      title: 'Untitled Product',
      subtitle: null,
      weight: null,
      staged: true,
      service_length_values: {},
      blocked_days: [],
      blocked_dates: [],
    };
  }
}
export default new EntityUtils();
